
import { computed, defineComponent, nextTick, onMounted, ref } from "vue";
import ViewHeader from "@/components/ViewHeader.vue";
import BackAndCancelFooter from "@/components/BackAndCancelFooter.vue";
import DropdownInput from "@/components/Inputs/DropdownInput.vue";
import TotalRow from "@/components/TotalRow.vue";
import { useStore } from "@/store";
import { CreditCardPayment } from "@/models/Order";
import { useRouter } from "vue-router";
import swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import NumberInput from "@/components/Inputs/NumberInput.vue";
import useVuelidate from "@vuelidate/core";
import { required, minValue, not, sameAs } from "@vuelidate/validators";
import { Validation } from "@/models/Validation";
import _, { isNumber } from "lodash";
import { CreditCardPaymentType, ToOptions } from "@/models/Enums";
import { api } from "@/services/Api";
import mixin from '@/mixin';
import Q from "q";

export default defineComponent({
  components: {
    ViewHeader,
    BackAndCancelFooter,
    DropdownInput,
    TotalRow,
    NumberInput,
  },
  props: {
    paymentIndex: {
      type: Number,
    },
  },
  async setup(props) {
    const store = useStore();
    const router = useRouter();
    const frameUrl = ref<string>("");
    const { t } = useI18n();
    const currencies = computed(() =>
      store.state.currencies.map((x) => {
        return { id: x.id, name: x.symbol };
      })
    );
    const orderTotal = computed(() => store.getters.orderTotal);
    const creditCardPaymentTypes = Object.entries(CreditCardPaymentType)
      .filter((x) => typeof x[0] === "string" && isNumber(x[1]))
      .map((x) => {
        return {
          id: x[1],
          name: t('cashier.credit-card-payment.' + x[0].toLowerCase()),
        };
      });

    const p = new CreditCardPayment();
    const payment =
      props.paymentIndex != undefined
        ? computed(() => store.state.order.payment.creditCardPayments.find((x) => x.index == props.paymentIndex) ?? new CreditCardPayment())
        : ref(p);
    const orderPayment = computed(() => store.state.order.payment);
    if (props.paymentIndex == undefined) {
      payment.value.amount = store.getters.orderBalance > 0 ? store.getters.orderBalance : 1;
      payment.value.currencyId = 1;
      payment.value.localCurrencyRate = store.state.currencyRates.find((x) => x.currencyId == payment.value.currencyId)?.rate ?? 1;
    }
    const total = computed(() => payment.value.amount * payment.value.localCurrencyRate);

    const submitForm = async (event) => {
      const valid = await v.value.$validate();
      if (!valid) {
        event.preventDefault();
        event.stopPropagation();
        swal.fire({
          icon: "info",
          text: t("alert.pleaseFillAllRequiredFields"),
        });
      }
    };

    async function clearCreditCard() {
      const valid = await v.value.$validate();
      if (!valid) {
        swal.fire({
          icon: "error",
          text: t("alert.pleaseFillAllRequiredFields"),
        });
        return;
      }
      const response = await api
        .withLoader()
        .PelecardInit(payment.value.amount, payment.value.currencyId, payment.value.creditCardPaymentType, payment.value.numberOfPayments);
      await Q.delay(400);
      if (response.error) {
        swal.fire({
          icon: "error",
          title: t("pelecard.error"),
          text: response.errorMessage,
        });
        return;
      }
      frameUrl.value = response.data?.url.toString() ?? "";
      payment.value.transactionId = response.data?.transactionId ?? 0;
    }

    onMounted(() => {
      nextTick(() => focusNext([0]));
    });

    function focusNext(indexes: number[]) {
      setTimeout(() => {

        for (const index of indexes) {
          const el = document.querySelector(`[data-index="${index + 1}"]`);
          console.log(el);
          if (el instanceof HTMLElement) {
            el.focus();
            return;
          }
        }
      }, 200)
    }

    function changeCurrency(e) {
      payment.value.currencyId = e.value;
      payment.value.localCurrencyRate = store.state.currencyRates.find((x) => x.currencyId == e.value)?.rate ?? 1;

      nextTick(() => focusNext([2]));
    }

    const minType = Number(_.minBy(creditCardPaymentTypes, (x) => x.id)?.id);
    const rules = {
      amount: { required, not: not(sameAs(0)) },
      currencyId: { required },
      localCurrencyRate: { required },
      creditCardPaymentType: { required, minValue: minValue(minType) },
      numberOfPayments: { required, minValue: minValue(1) },
    };

    const scope = "validationScope";
    const validation = new Validation(rules, scope);

    const v = useVuelidate({ $scope: scope });

    window.onmessage = async (e) => {
      if (e.data["PELECARD_CATCH_GOOD"]) {
        store.state.order.payment.addPayment(payment.value);
        router.replace({ name: "selectPayment" });
        return;
      }
      if (e.data["PELECARD_CATCH_ERROR"]) {
        await swal.fire({
          icon: "error",
          title: e.data["errorMessage"],
        });
        router.replace({ name: "selectPayment" });
        return;
      }
      if (e.data["PELECARD_CATCH_CANCEL"]) {
        await swal.fire({
          icon: "info",
          title: t("pelecard.payment-canceled"),
        });
        router.replace({ name: "selectPayment" });
        return;
      }
    };

    return {
      currencies,
      total,
      payment,
      submitForm,
      changeCurrency,
      orderTotal,
      orderPayment,
      v,
      validation,
      creditCardPaymentTypes,
      clearCreditCard,
      frameUrl,
      focusNext
    };
  },
});
